// frontend\src\components\CartaoModal\CartaoModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, InputNumber, message } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { createCartao, updateCartao } from '../../services/cardService';
import { getContas } from '../../services/accountService';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';

const { Option } = Select;

const CartaoModal = ({ isOpen, onClose, onSave, cartaoToEdit }) => {
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [form] = Form.useForm();
    const [contas, setContas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Hook de mensagem
    const [messageApi, contextHolder] = message.useMessage();

    const isEditMode = !!cartaoToEdit;

    useEffect(() => {
        if (isOpen) {
            // 1. Setup inicial síncrono para feedback imediato (evita form vazio piscando)
            form.resetFields();
            
            if (isEditMode && cartaoToEdit) {
                const pontosRaw = cartaoToEdit.pontos_por_unidade;
                const pontosNum =
                    pontosRaw != null && pontosRaw !== ''
                        ? Number(pontosRaw)
                        : undefined;

                form.setFieldsValue({
                    nome: cartaoToEdit.nome,
                    limite: cartaoToEdit.limite != null ? Number(cartaoToEdit.limite) : undefined,
                    bandeira: cartaoToEdit.bandeira || 'mastercard',
                    dia_fechamento: cartaoToEdit.dia_fechamento,
                    dia_vencimento: cartaoToEdit.dia_vencimento,
                    conta_associada: cartaoToEdit.conta_associada?.id || cartaoToEdit.conta_associada_id,
                    regra_pontos: cartaoToEdit.regra_pontos || 'NENHUMA',
                    pontos_por_unidade: Number.isFinite(pontosNum) ? pontosNum : undefined,
                });

                // Pré-carrega a conta atual na lista para o Select exibir o nome corretamente
                // antes mesmo da requisição ao servidor terminar.
                if (cartaoToEdit.conta_associada && typeof cartaoToEdit.conta_associada === 'object') {
                    setContas(prevContas => {
                         const exists = prevContas.some(c => c.id === cartaoToEdit.conta_associada.id);
                         return exists ? prevContas : [...prevContas, cartaoToEdit.conta_associada];
                    });
                }
            } else {
                form.setFieldsValue({
                    bandeira: 'mastercard',
                    regra_pontos: 'NENHUMA',
                    pontos_por_unidade: undefined,
                });
            }

            // 2. Busca a lista completa de contas em background
            const carregarContas = async () => {
                setIsLoading(true);
                
                try {
                    const data = await getContas();
                    setContas(data);
                } catch (err) {
                    console.error("Falha ao carregar contas", err);
                    messageApi.error('Não foi possível carregar a lista de contas.');
                } finally {
                    setIsLoading(false);
                }
            };
            carregarContas();
        }
    }, [isOpen, cartaoToEdit, form, isEditMode, messageApi]);

    const handleFormSubmit = async (closeOnSuccess = true) => {
        try {
            const values = await form.validateFields();
            setIsLoading(true);

            const regra = values.regra_pontos || 'NENHUMA';
            const dataToSubmit = {
                nome: values.nome,
                limite: values.limite,
                bandeira: values.bandeira,
                dia_fechamento: values.dia_fechamento,
                dia_vencimento: values.dia_vencimento,
                conta_associada_id: values.conta_associada,
                regra_pontos: regra,
                pontos_por_unidade: regra !== 'NENHUMA' ? values.pontos_por_unidade : null,
            };

            let successMsg = '';

            if (isEditMode) {
                await updateCartao(cartaoToEdit.id, dataToSubmit);
                successMsg = 'Cartão atualizado com sucesso!';
            } else {
                await createCartao(dataToSubmit);
                successMsg = 'Cartão adicionado com sucesso!';
            }
            
            onSave(closeOnSuccess, successMsg);

            if (!closeOnSuccess) {
                const currentConta = values.conta_associada;
                const currentBandeira = values.bandeira;

                form.resetFields();
                form.setFieldsValue({ 
                    conta_associada: currentConta,
                    bandeira: currentBandeira,
                    regra_pontos: 'NENHUMA',
                });
            }

        } catch (err) {
            console.error("Falha ao salvar cartão", err);

            let errorMsg = 'Verifique os dados e tente novamente.';
            
            if (err.response && err.response.data) {
                const data = err.response.data;

                if (typeof data === 'string' && (data.includes('<!DOCTYPE') || data.includes('<html'))) {
                    errorMsg = "Erro interno do sistema. Por favor, contate o suporte.";
                }
                else if (data.nome) {
                    errorMsg = data.nome[0]; 
                }
                else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const errorValue = data[firstKey];
                    errorMsg = Array.isArray(errorValue) ? `${firstKey}: ${errorValue.join(', ')}` : `${firstKey}: ${errorValue}`;
                }
            } else if (err.errorFields) {
                 setIsLoading(false);
                 return;
            }
            
            messageApi.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = () => handleFormSubmit(true);
    const handleSubmitAndAddNew = () => handleFormSubmit(false);

    const handleCancel = () => { !isLoading && onClose(); };

    const diasDoMesOptions = [...Array(31).keys()].map(i => {
        const day = i + 1;
        return <Option key={day} value={day}>{day}</Option>
    });

    const formContent = (
            <Form form={form} layout="vertical" preserve size={isMobile ? 'large' : 'middle'}>
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="nome" label="Nome do Cartão" rules={[{ required: true, message: 'O nome é obrigatório' }]}>
                            <Input placeholder="Ex: Cartão Nubank" />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="limite" label="Limite (R$)" rules={[{ required: true, message: 'O limite é obrigatório' }]}>
                            <InputNumber style={{ width: '100%' }} precision={2} step={100} formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)} parser={(value) => { if (!value) return ''; const onlyNumbers = value.replace(/[^\d]/g, ''); if (!onlyNumbers) return ''; const num = parseInt(onlyNumbers, 10); return num / 100; }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="bandeira" label="Bandeira" rules={[{ required: true, message: 'Selecione a bandeira' }]}>
                    <Select>
                        <Option value="mastercard">Mastercard</Option>
                        <Option value="visa">Visa</Option>
                        <Option value="elo">Elo</Option>
                        <Option value="amex">American Express</Option>
                        <Option value="hipercard">Hipercard</Option>
                        <Option value="outra">Outra</Option>
                    </Select>
                </Form.Item>

                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="dia_fechamento" label="Dia do Fechamento" rules={[{ required: true, message: 'O dia é obrigatório' }]}>
                            <Select placeholder="Selecione um dia">{diasDoMesOptions}</Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="dia_vencimento" label="Dia do Vencimento" rules={[{ required: true, message: 'O dia é obrigatório' }]}>
                            <Select placeholder="Selecione um dia">{diasDoMesOptions}</Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="conta_associada"
                    label="Conta para pagar a fatura"
                    extra="Usada geralmente ao pagar a fatura deste cartão — facilita o pagamento."
                    rules={[{ required: true, message: 'Selecione a conta para pagar a fatura' }]}
                >
                    <Select placeholder="Selecione uma conta..." loading={isLoading}>
                        {contas.map(conta => <Option key={conta.id} value={conta.id}>{conta.nome}</Option>)}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="regra_pontos"
                    label="Como este cartão pontua?"
                    extra="Usado para estimar pontos ao pagar a fatura (módulo Milhas)."
                    initialValue="NENHUMA"
                >
                    <Select>
                        <Option value="NENHUMA">Não pontua / Não configurado</Option>
                        <Option value="POR_DOLAR">Pontos por dólar</Option>
                        <Option value="POR_REAL">Pontos por real</Option>
                    </Select>
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.regra_pontos !== cur.regra_pontos}>
                    {({ getFieldValue }) => {
                        const regra = getFieldValue('regra_pontos');
                        const ativa = regra && regra !== 'NENHUMA';
                        const label = regra === 'POR_REAL' ? 'Pontos por real' : 'Pontos por dólar';
                        return (
                            <Form.Item
                                name="pontos_por_unidade"
                                label={label}
                                hidden={!ativa}
                                rules={
                                    ativa
                                        ? [{ required: true, message: `Informe os ${label.toLowerCase()}` }]
                                        : []
                                }
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0.1}
                                    step={0.1}
                                    precision={1}
                                    placeholder="Ex: 2.5"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>

            </Form>
    );

    if (isMobile) {
        return (
            <>
                {contextHolder}
                <MobileLaunchSheet
                    open={isOpen}
                    title={isEditMode ? 'Editar Cartão' : 'Novo Cartão'}
                    onCancel={handleCancel}
                    onSave={handleSubmit}
                    saveLoading={isLoading}
                    cancelDisabled={isLoading}
                    secondaryAction={
                        !isEditMode ? (
                            <Button type="default" loading={isLoading} onClick={handleSubmitAndAddNew} block size="large">
                                Salvar e Adicionar Outro
                            </Button>
                        ) : null
                    }
                >
                    {formContent}
                </MobileLaunchSheet>
            </>
        );
    }

    return (
        <Modal
            open={isOpen}
            title={isEditMode ? 'Editar Cartão de Crédito' : 'Adicionar Novo Cartão'}
            onCancel={handleCancel}
            destroyOnClose
            footer={[
                <Button key="back" onClick={handleCancel} disabled={isLoading}>Cancelar</Button>,
                !isEditMode && <Button key="saveAndNew" onClick={handleSubmitAndAddNew} disabled={isLoading}>Salvar e Adicionar Outro</Button>,
                <Button key="submit" type="primary" loading={isLoading} onClick={handleSubmit}>Salvar</Button>,
            ]}
        >
            {contextHolder}
            {formContent}
        </Modal>
    );
};

export default CartaoModal;
